import React, { useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { ArrowIosDownward } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Stack, Typography } from '@lyearn/ui';
import SingleSelectFilter from '@/components/FilterManager/SingleSelectFilter';
import { ListItemProps } from '@/components/SortMenu/types';

import { filterValueStyle } from '../styles';

type SingleSelectProps = {
  disabled?: boolean;
  options: ListItemProps[];
  filterValue: string | undefined | null;
  onChange: (value: string, item: ListItemProps) => void;
  error?: boolean;
};

function SingleSelect({ disabled, options, filterValue, onChange, error }: SingleSelectProps) {
  const { t } = useTranslation();
  const selectedValue = useMemo(() => (filterValue ? [filterValue] : undefined), [filterValue]);

  const selectedItemLabel = useMemo(
    () => options.find((option) => option.id === filterValue)?.label,
    [filterValue, options],
  );

  const hasError = useMemo(
    () => error && !disabled && !selectedValue,
    [disabled, error, selectedValue],
  );

  return (
    <Stack className={filterValueStyle} direction="col">
      <SingleSelectFilter
        disabled={disabled}
        items={options}
        selectedItems={selectedValue}
        onChange={onChange}>
        <Stack
          alignItems="center"
          className={cn(
            'h-40 w-full truncate rounded-8 border border-solid pe-12 ps-16',
            hasError ? 'border-error-dark' : 'border-interactive-white-hover',
          )}
          justifyContent="between">
          <Typography
            className="truncate"
            color={selectedItemLabel ? 'text-text-primary' : 'text-text-placeholder'}
            variant="body-short-02">
            {selectedItemLabel || t('Group:Editor.Value')}
          </Typography>
          <ArrowIosDownward
            className="h-20 w-20 flex-shrink-0 ms-12"
            fillColorToken="icon-placeholder"
          />
        </Stack>
      </SingleSelectFilter>
      {hasError ? (
        <Typography className="mt-8" color="text-text-error" variant="body-short-03">
          {t('Common:Form.Error.ValueCantBeEmpty')}
        </Typography>
      ) : null}
    </Stack>
  );
}

export default SingleSelect;
